import React, { JSX, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { format } from "date-fns/format";
import { isValid } from "date-fns/isValid";
import { parse } from "date-fns/parse";

import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import FormLabel from "@mui/material/FormLabel";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import InfoOutlined from "@mui/icons-material/InfoOutlined";

import { asTitle } from "utils/Utils";
import DatePicker from "utils/elements/DatePicker";

import HistoryDialog from "evaluation/HistoryDialog";
import { Competence, Properties, Property } from "trajectory/TrajectoryService";

import InfoDialog from "./InfoDialog";
import ScoreField from "./ScoreField";

export interface CompetenceScoreProps {
	depth: number;
	index: number;
	disabled: boolean;
	criteria: Competence;
	minScore: number;
	maxScore: number;
	editDate: Date | null;
	showFields: boolean;
	getScoreName: (score: number | null) => string; // function starts from minScore to maxScore
	getScoreInfo: (score: number | null) => string;
	getScoreColour: (score: number | null) => string;
}

const cellStyle = {
	borderBottom: "none"
};

const dropdownCellStyle = {
	verticalAlign: "top",
	borderBottom: "none",
	paddingBottom: 0,
	paddingLeft: 0,
	paddingRight: 1
};

const idCellStyle = {
	verticalAlign: "middle",
	borderBottom: "none",
	paddingBottom: 0,
	paddingLeft: 0,
	paddingRight: 1
};

const titleCellStyle = {
	width: 1,
	verticalAlign: "top",
	borderBottom: "none",
	paddingBottom: 0,
	paddingLeft: 0,
	paddingRight: 1
};

const scoreCellStyle = {
	verticalAlign: "top",
	borderBottom: "none",
	paddingBottom: 0,
	paddingLeft: 0,
	paddingRight: 1
};

const CompetenceScore: React.FC<CompetenceScoreProps> = (
	props: CompetenceScoreProps
) => {
	const { t } = useTranslation();

	const [showHistory, setShowHistory] = useState(false);
	const [newProperties, setNewProperties] = useState<Property>();

	const [showInfo, setShowInfo] = useState(false);

	useEffect(() => {
		const fixedFields = [
			"info",
			"planned_date",
			"location",
			"achieved",
			"offered"
		];

		if (props.criteria.latestProperties) {
			const hasUpdated =
				Object.keys(props.criteria.latestProperties)[0] !==
				Object.keys(props.criteria.properties)[0];

			const latestProperties = Object.values(
				props.criteria.latestProperties
			)[0];
			const defaultProperties = Object.entries(latestProperties)
				.filter(entry => hasUpdated || fixedFields.includes(entry[0]))
				.reduce(
					(object, value) => ({
						...object,
						[value[0]]: value[1]
					}),
					{}
				);
			setNewProperties(defaultProperties);
		} else {
			setNewProperties(undefined);
		}
	}, [props.disabled]);

	function getLatestProperties(): Property | null {
		if (props.disabled) {
			return props.criteria.latestProperties
				? Object.values(props.criteria.latestProperties)[0]
				: null;
		} else {
			return newProperties ?? null;
		}
	}

	function getProperty(name: string): string {
		const latestProperties = getLatestProperties();
		return latestProperties ? latestProperties[name] ?? "" : "";
	}

	function setProperty(name: string, value: string): void {
		const updatedProperties: Property = {
			...newProperties
		};
		updatedProperties[name] = value;

		if (
			props.depth === 2 &&
			name === "score" &&
			updatedProperties["date"] === undefined &&
			props.editDate !== null &&
			isValid(props.editDate)
		) {
			const date = new Date(props.editDate.getTime());
			date.setHours(0);
			date.setMinutes(0);
			date.setSeconds(0);
			date.setMilliseconds(0);
			updatedProperties["date"] = format(date, "dd/MM/yyyy");
		}

		const latestProperties: Properties = {};
		latestProperties[new Date().toISOString()] = updatedProperties;

		setNewProperties(updatedProperties);
		props.criteria.latestProperties = latestProperties;
	}

	function calculateLocationValue(
		school: boolean,
		werkplek: boolean,
		extern: boolean
	): string {
		let newValue = "";
		if (school) {
			newValue += "school";
		}

		if (werkplek) {
			if (newValue.length > 0) {
				newValue += ", ";
			}
			newValue += "werkplek";
		}

		if (extern) {
			if (newValue.length > 0) {
				newValue += ", ";
			}
			newValue += "extern";
		}

		return newValue;
	}

	function getImprovements(): string {
		const value = getProperty("remarks");
		const latestProperties = getLatestProperties();
		if (latestProperties && latestProperties["remarks"] !== undefined) {
			return value;
		}

		const defaultImprovements = `- Dit loopt al goed:
- Hier kan je nog in groeien:
- Daarvoor maken we volgende afspraken:`;
		return value !== "" ? value : props.disabled ? "" : defaultImprovements;
	}

	function stringToDate(value: string): Date | null {
		return value.length > 0 ? parse(value, "dd/MM/yyyy", new Date()) : null;
	}

	function updateHistoryLatestProperties(
		date: string,
		field: string,
		value: string
	): Properties | null {
		if (props.criteria.latestProperties) {
			const latestDate = Object.keys(props.criteria.latestProperties)[0];

			if (latestDate === date) {
				const latestProperties = props.criteria.latestProperties;
				const updatedLatestProperties = {
					...latestProperties
				};

				const latestProperty = latestProperties[date];
				const updatedLatestProperty = {
					...latestProperty
				};
				updatedLatestProperty[field] = value;
				updatedLatestProperties[date] = updatedLatestProperty;

				return updatedLatestProperties;
			}
		}

		return props.criteria.latestProperties;
	}

	function updateHistory(date: string, field: string, value: string): void {
		const properties = props.criteria.properties;
		const updatedProperties: Properties = {
			...properties
		};

		const property = properties[date];
		const updatedProperty: Property = {
			...property
		};
		updatedProperty[field] = value;
		updatedProperties[date] = updatedProperty;

		props.criteria.properties = updatedProperties;
		props.criteria.latestProperties = updateHistoryLatestProperties(
			date,
			field,
			value
		);
	}

	function deleteHistoryLatestProperties(date: string): Properties | null {
		if (props.criteria.latestProperties) {
			const latestDate = Object.keys(props.criteria.latestProperties)[0];

			if (latestDate === date) {
				// See if there is an older date.
				const dates = Object.keys(props.criteria.properties);
				if (dates.length <= 1) {
					// No previous properties;
					return null;
				}

				const newLatestProperties: Properties = {};
				newLatestProperties[dates[1]] =
					props.criteria.properties[dates[1]];

				return newLatestProperties;
			}
		}

		return props.criteria.latestProperties;
	}

	function deleteHistory(date: string): void {
		const properties = props.criteria.properties;
		const updatedProperties: Properties = {
			...properties
		};

		delete updatedProperties[date];

		props.criteria.properties = updatedProperties;
		props.criteria.latestProperties = deleteHistoryLatestProperties(date);
	}

	function updateDate(
		name: string,
		date: Date | null,
		update: (name: string, value: string) => void
	) {
		if (date !== null && isValid(date)) {
			date.setHours(0);
			date.setMinutes(0);
			date.setSeconds(0);
			date.setMilliseconds(0);

			update(name, format(date, "dd/MM/yyyy"));
		} else if (date === null) {
			update(name, "");
		}
	}

	function renderDatePicker(
		name: string,
		value: string,
		disabled: boolean,
		update: (name: string, value: string) => void
	): JSX.Element {
		const parsed = stringToDate(value);

		return (
			<DatePicker
				label={asTitle(t(name))}
				inputProps={{ variant: "standard" }}
				disabled={disabled}
				value={parsed}
				onChange={date => updateDate(name, date, update)}
			/>
		);
	}

	function renderField(
		name: string,
		value: string,
		disabled: boolean,
		update: (name: string, value: string) => void
	): JSX.Element {
		if (name === "achieved") {
			return (
				<FormGroup>
					<FormControlLabel
						control={
							<Checkbox
								checked={value === "true"}
								disabled={disabled}
								onChange={(event, checked): void => {
									update(
										"achieved",
										checked ? "true" : "false"
									);
								}}
								name="achieved"
							/>
						}
						label={asTitle(t("achieved"))}
					/>
				</FormGroup>
			);
		} else if (name === "location") {
			const schoolChecked = value.includes("school");
			const werkplekChecked = value.includes("werkplek");
			const externChecked = value.includes("extern");

			return (
				<FormControl variant="standard">
					<FormLabel>{asTitle(t("learnLocation"))}</FormLabel>
					<FormGroup row>
						<FormControlLabel
							control={
								<Checkbox
									checked={schoolChecked}
									disabled={disabled}
									onChange={(event, checked): void => {
										const school = checked;
										const werkplek = werkplekChecked;
										const extern = externChecked;

										const newValue = calculateLocationValue(
											school,
											werkplek,
											extern
										);
										update("location", newValue);
									}}
									name="school"
								/>
							}
							label={asTitle(t("school"))}
						/>
						<FormControlLabel
							control={
								<Checkbox
									checked={werkplekChecked}
									disabled={disabled}
									onChange={(event, checked): void => {
										const school = schoolChecked;
										const werkplek = checked;
										const extern = externChecked;

										const newValue = calculateLocationValue(
											school,
											werkplek,
											extern
										);
										update("location", newValue);
									}}
									name="workplace"
								/>
							}
							label={asTitle(t("werkplek"))}
						/>
						<FormControlLabel
							control={
								<Checkbox
									checked={externChecked}
									disabled={disabled}
									onChange={(event, checked): void => {
										const school = schoolChecked;
										const werkplek = werkplekChecked;
										const extern = checked;

										const newValue = calculateLocationValue(
											school,
											werkplek,
											extern
										);
										update("location", newValue);
									}}
									name="external"
								/>
							}
							label={asTitle(t("extern"))}
						/>
					</FormGroup>
				</FormControl>
			);
		} else if (name === "date") {
			return renderDatePicker(name, value, disabled, update);
		} else if (name === "score") {
			return (
				<ScoreField
					value={value}
					disabled={disabled}
					minScore={props.minScore}
					maxScore={props.maxScore}
					getScoreName={props.getScoreName}
					getScoreInfo={props.getScoreInfo}
					getScoreColour={props.getScoreColour}
					onChange={value => update("score", value)}
				/>
			);
		} else if (name === "remarks") {
			if (props.depth === 2) {
				return (
					<TextField
						multiline
						disabled={disabled}
						label={asTitle(t("improvements"))}
						InputLabelProps={{ shrink: true }}
						minRows={3}
						fullWidth={true}
						value={value}
						onChange={e => update("remarks", e.target.value)}
						variant="standard"
					/>
				);
			} else if (props.depth === 3) {
				return (
					<TextField
						multiline
						disabled={disabled}
						label={asTitle(t("remarks"))}
						minRows={2}
						fullWidth={true}
						value={value}
						onChange={e => update("remarks", e.target.value)}
						variant="standard"
					/>
				);
			}
		} else if (name === "offered") {
			return (
				<FormControlLabel
					labelPlacement="start"
					label={asTitle(t("offered"))}
					style={{ marginLeft: 0 }}
					control={
						<Checkbox
							disabled={disabled}
							checked={value === "true"}
							onChange={e =>
								update(
									"offered",
									e.target.checked ? "true" : "false"
								)
							}
						/>
					}
				/>
			);
		}

		return <></>;
	}

	function renderInlineField(
		name: string,
		caption?: string,
		value = getProperty(name)
	): JSX.Element {
		return (
			<Box key={name} sx={{ display: "flex", flexDirection: "column" }}>
				{caption !== undefined && (
					<Typography variant="caption" color="text.secondary">
						{caption}
					</Typography>
				)}
				{renderField(name, value, props.disabled, setProperty)}
			</Box>
		);
	}

	// Lays the small inputs out on a single wrapping row (reclaiming the empty
	// horizontal space) with the wide field (remarks/info) full-width beneath.
	// Replaces the previous one-TableRow-per-field stacking.
	function renderFieldGroup(
		inline: JSX.Element[],
		below?: JSX.Element
	): JSX.Element {
		return (
			<TableRow>
				<TableCell sx={cellStyle} colSpan={2} />
				<TableCell sx={cellStyle} colSpan={2}>
					{renderLabels()}
					{inline.length > 0 && (
						<Box
							sx={{
								display: "flex",
								flexWrap: "wrap",
								alignItems: "flex-start",
								columnGap: 4,
								rowGap: 2
							}}>
							{inline}
						</Box>
					)}
					{below && (
						<Box sx={{ marginTop: inline.length > 0 ? 2 : 0 }}>
							{below}
						</Box>
					)}
				</TableCell>
			</TableRow>
		);
	}

	function renderFields(): JSX.Element {
		if (props.depth === 2) {
			return renderFieldGroup(
				[
					renderInlineField("achieved"),
					renderInlineField("location"),
					renderInlineField("date"),
					renderInlineField("score", asTitle(t("rating")))
				],
				renderField(
					"remarks",
					getImprovements(),
					props.disabled,
					setProperty
				)
			);
		} else if (props.depth === 3) {
			return renderFieldGroup(
				[renderInlineField("offered")],
				renderField(
					"remarks",
					getProperty("remarks"),
					props.disabled,
					setProperty
				)
			);
		}

		return renderFieldGroup([]);
	}

	function renderLabels(): JSX.Element {
		if (props.criteria.labels.length === 0) {
			return <></>;
		}

		return (
			<Box sx={{ marginBottom: 1 }}>
				{props.criteria.labels.map(label => (
					<Chip
						key={label}
						label={label}
						sx={{ marginInlineEnd: "1em" }}
					/>
				))}
			</Box>
		);
	}

	const onShowHistory = useCallback(() => {
		setShowHistory(true);
	}, []);

	const onHideHistory = useCallback(() => {
		setShowHistory(false);
	}, []);

	const onHideInfo = useCallback(() => {
		setShowInfo(false);
	}, []);

	const onShowInfo = useCallback(() => {
		setShowInfo(true);
	}, []);

	return (
		<>
			<TableRow>
				<TableCell sx={dropdownCellStyle} />
				<TableCell sx={idCellStyle}>
					<Typography variant="h6">{props.index + 1}</Typography>
				</TableCell>
				<TableCell sx={titleCellStyle}>
					<Grid container alignItems="center" columnSpacing={1}>
						<Grid item>
							{props.criteria.info !== "" && (
								<IconButton size="small" onClick={onShowInfo}>
									<InfoOutlined />
								</IconButton>
							)}
						</Grid>
						<Grid item>
							{props.showFields && (
								<Typography
									variant="h6"
									style={{ cursor: "pointer" }}
									onClick={onShowHistory}>
									{props.criteria.name}
								</Typography>
							)}

							{!props.showFields && (
								<Typography variant="h6">
									{props.criteria.name}
								</Typography>
							)}
						</Grid>
					</Grid>
				</TableCell>
				<TableCell sx={scoreCellStyle} />
			</TableRow>
			{props.showFields && renderFields()}

			{showHistory && (
				<HistoryDialog
					competence={props.criteria}
					editable={!props.disabled}
					getField={renderField}
					updateField={updateHistory}
					deleteEvaluation={deleteHistory}
					onClose={onHideHistory}
				/>
			)}

			{showInfo && (
				<InfoDialog info={props.criteria.info} onClose={onHideInfo} />
			)}
		</>
	);
};

export default CompetenceScore;
